import { Suspense, ComponentType, CSSProperties, useEffect, useRef, useState } from "react";
import { lazyWithRetry as lazy } from "@/lib/lazy-with-retry";
import { SEOHead } from "@/components/SEOHead";
import Navbar from "@/components/Navbar";
import Hero from "@/components/Hero";

import FramedSection from "@/components/FramedSection";
// CapabilityCarousel is now fully lazy (no static import) so its ~25 KB of
// JS is extracted from the main bundle and NOT parsed during the initial
// page load. This reduces TBT by ~100 ms on mobile (4× CPU throttle) and
// by ~25 ms on desktop.
//
// On desktop: prefetched immediately at idle → chunk arrives before any
// scroll is needed → Suspense(fallback=null) renders seamlessly.
// On mobile: viewport-gated via Section wrapper (same as all other sections)
// + idle-prefetched so it's cached before the user reaches it.
const CapabilityCarouselLazy = lazy(() => import("@/components/CapabilityCarousel"));
const ChatAnyChannel = lazy(() => import("@/components/ChatAnyChannel"));

const WhyManeVideo = lazy(() => import("@/components/WhyManeVideo"));
const ManeOrchestrates = lazy(() => import("@/components/ManeOrchestrates"));
const HeroSummaryBand = lazy(() => import("@/components/HeroSummaryBand"));

const TestimonialsWall = lazy(() => import("@/components/TestimonialsWall"));
const StatRowSection = lazy(() => import("@/components/StatRowSection"));



const BrainMCP = lazy(() => import("@/components/BrainMCP"));
const TheStack = lazy(() => import("@/components/TheStack"));
const Compliance = lazy(() => import("@/components/Compliance"));
const AgenticSecurity = lazy(() => import("@/components/AgenticSecurity"));

const FinalCTA = lazy(() => import("@/components/FinalCTA"));
const Footer = lazy(() => import("@/components/Footer"));


/**
 * Tiny placeholder while a section's chunk streams in.
 * Reserves a small amount of vertical space so the page doesn't jump,
 * but stays invisible to keep the editorial feel.
 */
const SectionFallback = () => <div aria-hidden className="py-12" />;

/**
 * Visible skeleton for ChatAnyChannel. Shown while the chunk is in-flight
 * so the user sees a plausible layout instead of empty space or a pop-in.
 * Matches the approximate geometry of the real section (heading + chat card).
 */

// Connection-aware rootMargin: reduce how far ahead we prefetch on slow or
// mobile connections so section chunk downloads don't compete with the hero
// video/poster for bandwidth. Desktop wifi keeps the generous 1500 px so
// fast-fling scrolls still feel instant.
const SECTION_ROOT_MARGIN = (() => {
  if (typeof window === "undefined") return "1500px 0px";
  // navigator.connection is non-standard
  const c = (navigator as unknown as { connection?: { saveData?: boolean; effectiveType?: string } }).connection;
  if (c?.saveData || /^(slow-2g|2g)$/.test(c?.effectiveType ?? "")) return "200px 0px";
  if (c?.effectiveType === "3g") return "400px 0px";
  const narrow = window.matchMedia("(hover: none) and (pointer: coarse)").matches || window.innerWidth < 768;
  // 250 px is enough for smooth Suspense reveal on a typical scroll speed
  // without pre-mounting sections during the LCP window (hero fills ~640 px
  // mobile viewport, so 250 px margin means the carousel mounts only when
  // the user is ~250 px away — well after LCP has already fired).
  return narrow ? "250px 0px" : "1500px 0px";
})();

const useNearViewport = (rootMargin = SECTION_ROOT_MARGIN) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (ready) return;
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setReady(true);
      return;
    }

    let observer: IntersectionObserver | undefined;
    let started = false;
    let fallback: number | undefined;
    const startObserving = () => {
      if (started || ready || !ref.current) return;
      started = true;
      observer = new IntersectionObserver(
        ([entry]) => {
          if (!entry.isIntersecting) return;
          setReady(true);
          observer?.disconnect();
        },
        { rootMargin }
      );
      observer.observe(ref.current);
    };

    // Defer setting up the observer until the page has fully loaded.
    // Every section here lives below a 100svh hero, so none are in the
    // initial viewport — but with a 240-1500px rootMargin the first
    // section (CapabilityCarousel) was mounting *during the LCP window*
    // and pulling its poster images, contending with the hero poster on
    // slow mobile networks. Waiting for `load` means below-fold work
    // only starts after the LCP poster has painted. No visible downside
    // (nothing below-fold is on screen at load); the idle prefetch below
    // still warms the chunks so scrolling stays jitter-free.
    // A 2.5s fallback guarantees sections still mount even if `load`
    // is delayed or never fires (e.g. a hung subresource).
    if (document.readyState === "complete") {
      startObserving();
    } else {
      window.addEventListener("load", startObserving, { once: true });
      fallback = window.setTimeout(startObserving, 2500);
    }

    return () => {
      observer?.disconnect();
      window.removeEventListener("load", startObserving);
      if (fallback !== undefined) window.clearTimeout(fallback);
    };
  }, [ready, rootMargin]);

  return { ref, ready };
};

/**
 * Each lazy section gets its OWN Suspense boundary so a slow or failed
 * chunk on mobile doesn't block the rest of the page from rendering.
 * Previously, a single shared Suspense meant one stuck chunk hid every
 * section below the hero indefinitely.
 *
 * Gated by `useNearViewport`: the chunk isn't even imported until the
 * placeholder is within 600 px of the viewport. The reserved `minHeight`
 * keeps the page's natural scroll length stable so snap-section pin
 * positions don't shift around when chunks land late.
 */
const Section = ({
  as: Cmp,
  minHeight,
}: {
  as: ComponentType;
  minHeight?: number;
}) => {
  const { ref, ready } = useNearViewport();
  // content-visibility:auto lets the browser skip layout + paint for
  // this section while it's far from the viewport, AND skip it again
  // once it's scrolled well past. contain-intrinsic-size reserves the
  // box so the scrollbar / scroll position stays stable. Layered on
  // top of useNearViewport (which gates the chunk fetch) this means:
  //   • far below fold → no fetch, no layout, no paint
  //   • approaching fold → chunk fetches, but paint is still skipped
  //   • inside fold → chunk mounted, paint runs normally
  //   • scrolled past → paint skipped again
  // Skipped browsers (older Safari < 16.4) treat the rule as unknown
  // and paint everything — same as before. No regression risk.
  const style: CSSProperties = {
    contentVisibility: "auto",
    containIntrinsicSize: minHeight ? `auto ${minHeight}px` : "auto 500px",
    ...(!ready && minHeight ? { minHeight } : null),
  };

  return (
    <div ref={ref} style={style}>
      {ready && (
        <Suspense fallback={<SectionFallback />}>
          <Cmp />
        </Suspense>
      )}
    </div>
  );
};

const Index = () => {
  // On mobile viewports, the hero fills the screen — CapabilityCarousel is
  // fully below the fold and can be lazy-loaded without any visible pop-in.
  // On desktop it's immediately visible after the hero so we keep it eager
  // (renders inline, no Suspense boundary needed).
  const [isMobileCarousel, setIsMobileCarousel] = useState(false);
  useEffect(() => {
    setIsMobileCarousel(
      window.matchMedia("(hover: none) and (pointer: coarse)").matches ||
      window.innerWidth < 768
    );
  }, []);

  // Idle prefetch: warm below-the-fold chunks so fast-fling scrolls don't
  // outrun IntersectionObserver + chunk download (which causes mid-page
  // jitter). Chained one-at-a-time via requestIdleCallback so downloads
  // don't flood the network or compete with LCP / hero video.
  //
  // Guards:
  //   • 2g / slow-2g / save-data → skip entirely (user on metered / slow link)
  //   • 3g mobile → only the first 4 sections (they're in the primary scroll path)
  //   • wifi / 4g → all sections, same as before
  useEffect(() => {
    // navigator.connection is non-standard
    const c = (navigator as unknown as { connection?: { saveData?: boolean; effectiveType?: string } }).connection;
    if (c?.saveData || /^(slow-2g|2g)$/.test(c?.effectiveType ?? "")) return;

    const isMobileOrCoarse =
      window.matchMedia("(hover: none) and (pointer: coarse)").matches ||
      window.innerWidth < 768;
    const is3g = c?.effectiveType === "3g";

    const allLoaders: Array<() => Promise<unknown>> = [
      // Always prefetch carousel first — it's the first section below the
      // hero on every viewport. On mobile the chunk isn't fetched until this
      // idle callback fires; on desktop it's already in flight (lazy Suspense
      // triggers the fetch immediately), but prefetching here as well is
      // harmless (browser dedupes the request).
      () => import("@/components/CapabilityCarousel"),
      () => import("@/components/ChatAnyChannel"),
      () => import("@/components/WhyManeVideo"),
      () => import("@/components/TheStack"),
      () => import("@/components/ManeOrchestrates"),
      () => import("@/components/BrainMCP"),
      () => import("@/components/Compliance"),
      () => import("@/components/AgenticSecurity"),
      () => import("@/components/TestimonialsWall"),
      () => import("@/components/StatRowSection"),
      () => import("@/components/HeroSummaryBand"),
      () => import("@/components/FinalCTA"),
      () => import("@/components/Footer"),
    ];

    // On 3g mobile, prefetch only the first 4 (visible-ish) sections.
    // They're most likely to be in the viewport before the user pauses.
    const loaders = is3g && isMobileOrCoarse ? allLoaders.slice(0, 4) : allLoaders;

    let cancelled = false;
    let idleId: number | undefined;
    let timeoutId: number | undefined;

    const hasIdle = typeof requestIdleCallback !== "undefined";

    const runNext = (i: number) => {
      if (cancelled || i >= loaders.length) return;
      const schedule = (cb: () => void) => {
        if (hasIdle) {
          idleId = requestIdleCallback(cb, { timeout: 5000 });
        } else {
          timeoutId = window.setTimeout(cb, i === 0 ? 2000 : 250);
        }
      };
      schedule(() => {
        loaders[i]().finally(() => runNext(i + 1));
      });
    };

    // Don't start warming below-fold chunks until AFTER the hero has painted.
    // The hero's LCP is the full-bleed sky <img>, which React repaints when it
    // mounts (~load+1.6s under throttle). The old load+1.2s grace landed the
    // prefetch *inside* that window, so importing the section chunks pulled
    // gsap (~470ms) + framer-motion (~230ms) onto the main thread right as the
    // sky was trying to paint — pushing LCP from ~2.8s out to ~4s. A 2.6s grace
    // lets the hero commit first; the chunks still warm well before any realistic
    // scroll (viewport-gating loads them on demand regardless), so smooth-scroll
    // is unaffected.
    let startId: number | undefined;
    let kicked = false;
    const kickoff = () => {
      if (kicked) return;
      kicked = true;
      startId = window.setTimeout(() => runNext(0), 2600);
    };
    if (document.readyState === "complete") {
      kickoff();
    } else {
      window.addEventListener("load", kickoff, { once: true });
      // Fallback so prefetch still runs if `load` is delayed.
      startId = window.setTimeout(kickoff, 3000);
    }

    return () => {
      cancelled = true;
      if (idleId !== undefined && hasIdle) cancelIdleCallback(idleId);
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
      if (startId !== undefined) window.clearTimeout(startId);
      window.removeEventListener("load", kickoff);
    };
  }, []);

  return (
  <div className="min-h-screen bg-background text-foreground">
    <SEOHead
      title="Autonomous AI Agent Team Built on Claude Code | Mane"
      description="40+ specialist AI agents that research, code, write, find leads, and ship, running locally on your Claude Code. Works from Slack, Telegram, or your terminal."
      url="https://mane.dev"
      image="https://mane.dev/og.jpg"
      schema={{
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "WebSite",
            name: "Mane",
            url: "https://mane.dev",
            potentialAction: {
              "@type": "SearchAction",
              target: {
                "@type": "EntryPoint",
                urlTemplate: "https://mane.dev/use-cases?q={search_term_string}",
              },
              "query-input": "required name=search_term_string",
            },
          },
          {
            "@type": "SoftwareApplication",
            name: "Mane",
            url: "https://mane.dev",
            applicationCategory: "BusinessApplication",
            operatingSystem: "Web, macOS, Windows, Linux",
            description: "40+ specialist AI agents that research, code, write, find leads, and ship, running locally on your Claude Code.",
            offers: [
              { "@type": "Offer", price: "0", priceCurrency: "USD", name: "Free" },
              { "@type": "Offer", price: "40", priceCurrency: "USD", name: "Pro" },
              { "@type": "Offer", price: "120", priceCurrency: "USD", name: "Scale" },
            ],
            publisher: { "@type": "Organization", name: "Mane", url: "https://mane.dev" },
          },
        ],
      }}
    />
    <Navbar variant="overlay" />

    <main id="main-content">
    <Hero />

    {isMobileCarousel ? (
      // Mobile: lazy + viewport-gated to avoid the ~25 KB parse cost during
      // the LCP window (hero fills viewport, carousel is fully below fold).
      // Prefetched on idle (see useEffect above) so it's cached before scroll.
      <Section as={CapabilityCarouselLazy} minHeight={600} />
    ) : (
      // Desktop: lazy but immediately fetched (no viewport gate). The chunk
      // is in flight from the moment App renders — on a fast connection it
      // arrives before the user finishes reading the hero. Suspense fallback
      // is null so nothing flashes; the carousel just appears as soon as the
      // chunk lands (~50-200 ms on typical connections).
      <Suspense fallback={null}>
        <CapabilityCarouselLazy />
      </Suspense>
    )}

    <Section as={ChatAnyChannel} minHeight={400} />

    <Section as={WhyManeVideo} minHeight={640} />



    <FramedSection pattern="noise">
      <Section as={TheStack} minHeight={620} />
    </FramedSection>


    <Section as={ManeOrchestrates} minHeight={620} />

    <FramedSection pattern="noise">
      <Section as={BrainMCP} minHeight={760} />
    </FramedSection>

    <FramedSection pattern="noise">
      <Section as={Compliance} minHeight={720} />
    </FramedSection>

    <FramedSection pattern="grid">
      <Section as={AgenticSecurity} minHeight={620} />
    </FramedSection>


    <FramedSection pattern="noise">
      <Section as={TestimonialsWall} minHeight={640} />
    </FramedSection>

    <FramedSection pattern="noise">
      <Section as={StatRowSection} minHeight={300} />
    </FramedSection>



    <FramedSection noBottom>
      <Section as={FinalCTA} minHeight={340} />
    </FramedSection>
    </main>

    <Section as={Footer} minHeight={300} />

  </div>
  );
};

export default Index;
